/**
 * 联网搜索工具层类型契约（T12a）。
 *
 * 唯一事实来源：docs/CODE-TASKS.md 第 5 节「核心契约」与需求第 9 条「联网搜索」。
 *
 * 设计约定（与 T03 Provider 层保持同构，便于上层复用同一套心智模型）：
 * - 「持久化配置」（`SearchConfig`）与「运行期已解析配置」（`ResolvedSearchConfig`）分离：
 *   密钥只以 `apiKeyRef` 引用存储，真实 `apiKey` 由上层密钥存储解析后注入，本层不读 localStorage；
 * - 错误码是可枚举的字面量联合（`SearchErrorCode`），风格与 T03 的 `ProviderErrorCode` 一致，
 *   上层可按 `code` 分流重试 / 提示 / 取消；
 * - 所有可选字段都允许缺失，解析层对脏数据必须「不崩、不抛」，由 `tavily.ts` 归一化。
 */

import type { FetchLike } from '../providers/types';

/** 搜索后端种类。`custom` 预留给自建 / 未内置的搜索服务。 */
export type SearchProviderKind = 'tavily' | 'brave' | 'searxng' | 'custom';

/** 单条搜索结果。标题 / 链接 / 摘要必填（缺失时由解析层补空串），发布时间可选。 */
export interface SearchResult {
  title: string;
  url: string;
  snippet: string;
  publishedAt?: string;
}

/** 一次搜索请求。`limit` 为期望条数（后端可返回更少）；`signal` 用于取消。 */
export interface SearchRequest {
  query: string;
  limit?: number;
  signal?: AbortSignal;
}

/**
 * 搜索后端统一接口。
 *
 * 契约：取消（`SearchRequest.signal` 触发）时以 `SearchError{ code: 'aborted' }` 拒绝，
 * 而不是返回部分结果或静默成功——`search()` 返回 Promise，没有「静默结束」的语义。
 */
export interface SearchProvider {
  readonly id: string;
  readonly kind: SearchProviderKind;
  search(req: SearchRequest): Promise<SearchResult[]>;
}

/** 持久化的搜索后端配置。密钥只存引用，不存明文。 */
export interface SearchConfig {
  id: string;
  kind: SearchProviderKind;
  name: string;
  baseUrl: string;
  apiKeyRef: string;
  enabled: boolean;
}

/** 运行期已解析出密钥的配置（`apiKeyRef` -> `apiKey` 由上层密钥存储负责）。 */
export interface ResolvedSearchConfig extends SearchConfig {
  apiKey?: string;
  /** 追加到默认请求头上的自定义头。 */
  headers?: Record<string, string>;
  /** 覆盖默认超时（毫秒）。 */
  timeoutMs?: number;
  /** 覆盖默认重试次数（不含首次请求）。 */
  retries?: number;
  /** 覆盖默认重试间隔（毫秒）。 */
  retryDelayMs?: number;
}

/**
 * 搜索层错误码。取值与 T03 `ProviderErrorCode` 逐一对齐，
 * 便于上层用同一套分流逻辑处理「模型调用」与「联网搜索」。
 */
export type SearchErrorCode =
  'http' | 'timeout' | 'network' | 'aborted' | 'parse' | 'config' | 'unsupported' | 'unknown';

/** 运行时枚举（便于排错页 / 日志全量列出错误码）。 */
export const SEARCH_ERROR_CODES: readonly SearchErrorCode[] = [
  'http',
  'timeout',
  'network',
  'aborted',
  'parse',
  'config',
  'unsupported',
  'unknown',
] as const;

/** 搜索层统一错误：携带机器可判别的 `code` 与可选的 HTTP `status`。 */
export class SearchError extends Error {
  readonly code: SearchErrorCode;
  readonly status?: number;

  constructor(message: string, code: SearchErrorCode, status?: number) {
    super(message);
    this.name = 'SearchError';
    this.code = code;
    this.status = status;
  }
}

/** 可注入的 fetch 实现（默认取 `globalThis.fetch`，由 http.ts 解析）。 */
export type { FetchLike };

/** 搜索适配器可复用的运行期依赖，字段与 T03 `ProviderDeps` 一致。 */
export interface SearchDeps {
  fetch?: FetchLike;
  timeoutMs?: number;
  retries?: number;
  retryDelayMs?: number;
  extraHeaders?: Record<string, string>;
}

/** 适配器工厂：注册表按 `SearchConfig.kind` 调用它实例化适配器。 */
export type SearchProviderFactory = (
  config: ResolvedSearchConfig,
  deps?: SearchDeps,
) => SearchProvider;
